import { observer } from "mobx-react";
import { CustomDialog } from "../../../../shared/views/CustomDialog";
import {
  Alert,
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Spacer,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useStores } from "../../../../shared/stores/RootStore";
import { useEffect, useState } from "react";
import mapboxgl from "mapbox-gl";
import { mapUtils } from "../../../../map/utils/map-utils";
import { CutHlaAnchor, CutHlaMapManager } from "./CutHlaMapManager";
import { DeleteIcon } from "../../../../shared/views/icons";
import turf from "../../../../shared/utils/turf";
import { Feature, LineString } from "geojson";
import { toast } from "../../../../shared/views/CustomToaster";

const mapId = "cutHlaMap";

const CutHlaModaBase = ({ onClose }: { onClose: () => void }) => {
  const { hlaStore } = useStores();
  const way = hlaStore.getSelected()!;

  const [submitting, setSubmitting] = useState(false);
  const [manager, setManager] = useState<CutHlaMapManager>();
  const [anchors, setAnchors] = useState<CutHlaAnchor[]>([]);

  useEffect(() => {
    const map = new mapboxgl.Map({
      ...mapUtils.defaultConfig,
      container: mapId,
    });

    const manager = new CutHlaMapManager(map, way.coordinates);

    manager.setOnAnchorSelected((newAnchor) => {
      setAnchors((prev) =>
        [...prev, newAnchor].toSorted(
          (a, b) => a.distanceMeters - b.distanceMeters,
        ),
      );
    });

    setManager(manager);

    return () => {
      manager.clear();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    manager?.setAnchors(anchors);

    // eslint-disable-next-line
  }, [anchors]);

  const submit = () => {
    setSubmitting(true);
    const lineString = turf.lineString(way.coordinates);

    const newLines = splitLineAtDistances(
      lineString,
      anchors.map((a) => a.distanceMeters),
    ).map((feature) =>
      feature.geometry.coordinates.map(
        (point) =>
          ({
            lat: point[1],
            lng: point[0],
          } as mapboxgl.LngLat),
      ),
    );

    hlaStore
      .cutSelectedWay(newLines)
      .then(() => {
        toast("success", "Cut successful");
        onClose();
      })
      .catch(() => {
        toast("error", "Cut failed");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <CustomDialog
      size="xl"
      onClose={onClose}
      header={"Cut segment"}
      footer={
        <HStack flexGrow={1}>
          <Spacer />
          <Button colorPalette={"primary"} variant={"ghost"} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorPalette={"primary"}
            disabled={anchors.length === 0}
            onClick={submit}
            loading={submitting}
          >
            Apply
          </Button>
        </HStack>
      }
    >
      <HStack alignItems="stretch" gap={6} height="500px">
        <Stack width={"300px"}>
          <Text mb={1}>
            Segment length: {manager?.segmentLength.toFixed(2)} m
          </Text>

          {anchors.length === 0 && (
            <Alert.Root status={"info"}>
              <Alert.Indicator />
              <Alert.Description>
                Click the line to start cutting
              </Alert.Description>
            </Alert.Root>
          )}

          {anchors.map((anchor) => (
            <HStack key={anchor.distanceMeters}>
              <Text flexGrow={1}>
                Cut at {anchor.distanceMeters.toFixed(2)} m
              </Text>
              <IconButton
                size="xs"
                variant={"surface"}
                colorPalette={"red"}
                onClick={() => {
                  setAnchors((prev) => prev.filter((a) => a !== anchor));
                }}
              >
                <Icon as={DeleteIcon} />
              </IconButton>
            </HStack>
          ))}
        </Stack>

        <Box flexGrow={1} id={mapId} />
      </HStack>
    </CustomDialog>
  );
};

export const CutHlaModal = observer(CutHlaModaBase);

function splitLineAtDistances(
  line: Feature<LineString>,
  breakDistances: number[],
): Feature<LineString>[] {
  const totalLength = turf.length(line, { units: "meters" });

  // Sort and clamp distances, bookend with 0 and total length
  const distances = [
    0,
    ...breakDistances
      .filter((d) => d > 0 && d < totalLength)
      .sort((a, b) => a - b),
    totalLength,
  ];

  // Convert distances to points along the line
  const breakpoints = distances.map((d) =>
    turf.along(line, d, { units: "meters" }),
  );

  // Slice between consecutive breakpoints
  const segments = [];
  for (let i = 0; i < breakpoints.length - 1; i++) {
    const segment = turf.lineSlice(breakpoints[i], breakpoints[i + 1], line);
    segments.push(segment);
  }

  return segments;
}
